import { truncate } from '@r1-frontend/shared/helpers/truncate';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import Card from '~/src/components/Card';
import CategoryBlock from '~/src/components/ProductCard/blocks/CategoryBlock';
import HeaderBlock from '~/src/components/ProductCard/blocks/HeaderBlock';
import ImageBlock from '~/src/components/ProductCard/blocks/ImageBlock';
import PurchaseBlock from '~/src/components/ProductCard/blocks/PurchaseBlock';
import TitleBlock from '~/src/components/ProductCard/blocks/TitleBlock';
import { EPriceOperationTypes } from '~/src/entities/price';
import { isCreditAbnEnabled } from '~/src/features/work-in-home-ab/components/Description/components/Credit/constants/abnVariants';

import { MinPriceTitle, SpecOption } from './styled';

const DEFAULT_DESCRIPTION = 'Бесплатно доставим и настроим';
const SPEC_NAME_SPEED = 'speed';
const SPEC_NAME_AREA = 'area';
const SPEC_NAME_DEVICES = 'devices';
const ROUTERS_CATEGORY = 'routers';

const ProductCard = (
    {
        city,
        dataTest,
        categoryTitle = '',
        categoryName = '',
        description = DEFAULT_DESCRIPTION,
        image,
        imageWebp,
        labels = [],
        name = '',
        shortName = '',
        orderHandler = () => ({}),
        detailHandler,
        orderText = 'Заказать',
        priceOptions = [],
        route,
        body,
        spec = [],
        withSelect = true,
        backgroundColorData = {},
        isDarkTheme = false,
        baseRoute,
        buttonLink = '',
        setIndexOfSelected,
        isSmallCards = false,
        linkUrl = false,
    },
) => {
    const [selectedOption, setSelectedOption] = useState(() =>
        isCreditAbnEnabled()
            ? priceOptions.find(option => option.operType === EPriceOperationTypes.Possess)
            : priceOptions[0]);

    const [loading, setLoading] = useState({
        orderStatus: false,
        isLoadingOrder: false,
    });

    useEffect(() => {
        if (loading.isLoadingOrder === true) {
            orderHandler(selectedOption)
                .then(() => {
                    setLoading({ ...loading, isLoadingOrder: false });
                });
        }
    }, [loading]);

    const checkSpecs =  (specItem) => {
        return specItem.name === SPEC_NAME_SPEED || specItem.name === SPEC_NAME_AREA || specItem.name === SPEC_NAME_DEVICES;
    };

    const renderSpecs = useCallback(() => {
        return (
            <div>
            {spec.map((item, i) => {
                return  checkSpecs(item) && <SpecOption key={i} isDarkTheme={isDarkTheme}>
                            <span>{item.name === SPEC_NAME_SPEED ? item.title.split(' ')[0] : item.title}</span>
                            <span>{item.value}</span>
                        </SpecOption>;
                })}
            </div>
        );
    }, [spec]);

    const hasOneOfSpec = spec.filter(checkSpecs).length > 0;
    const isRouters = categoryName === ROUTERS_CATEGORY;
    const descriptionPrepared = (city.hasFreeDeliveryAndInstallation || description !== DEFAULT_DESCRIPTION)
        ? description
        : '';

    const openInNewTab = (url) => {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
            newWindow.opener = null;
        }
    };

    const onClickUrl = (url) => {
        return url
            ? () => openInNewTab(url)
            : detailHandler;
    };

    const minPriceTitle = useMemo(() => {
        const result = [...priceOptions].sort((a, b) => a.price - b.price)[0]?.price;
        return result ? `от ${result} ₽/мес` : '';
    }, [priceOptions]);

    const prepareBody = useMemo(() => {
        if (!body) {
            return descriptionPrepared;
        }
        const regex = new RegExp('<p[^>]*>(.+)</p>', 'g');
        const prepared = body.replace(regex, '$1');
        return `<p>${truncate(prepared, 90)}</p>`;
    }, [body]);

    return (
        <Card
            dataTest={'router-item'}
            backgroundColorData={backgroundColorData}
            isDarkTheme={isDarkTheme}
            isSmallCards={isSmallCards}
            headerBlock={
                !isSmallCards && <HeaderBlock
                    labels={labels}
                />
            }
            imageBlock={
                <ImageBlock
                    route={route}
                    image={image}
                    imageWebp={imageWebp}
                    name={name}
                />
            }
            categoryBlock={
                !isSmallCards && <CategoryBlock
                    name={categoryTitle}
                />
            }
            titleBlock={
                <TitleBlock
                    route={route}
                    name={shortName || name}
                    isSmallCards={isSmallCards}
                />
            }
            descriptionBlock={
                !isSmallCards && isRouters && hasOneOfSpec
                    ? renderSpecs()
                    : isSmallCards
                        ? <MinPriceTitle>
                            {minPriceTitle}
                        </MinPriceTitle>
                        : <div dangerouslySetInnerHTML={{ __html: prepareBody }}/>
            }
            purchaseBlock={
                !isSmallCards && <PurchaseBlock
                    dataTest={dataTest}
                    orderClickHandler={onClickUrl(buttonLink)}
                    name={name}
                    loading={loading}
                    priceOptions={priceOptions}
                    orderText={orderText}
                    withSelect={withSelect}
                    selectedOption={selectedOption}
                    setLoading={setLoading}
                    setSelectedOption={setSelectedOption}
                    isDarkTheme={isDarkTheme}
                    baseRoute={baseRoute}
                    setIndexOfSelected={setIndexOfSelected}
                    linkUrl={linkUrl}
                />
            }
        />
    );
};

ProductCard.propTypes = {
    categoryTitle: PropTypes.string,
    categoryName: PropTypes.string,
    name: PropTypes.string,
    orderHandler: PropTypes.func,
    detailHandler: PropTypes.func,
    labels: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            title: PropTypes.string,
            color: PropTypes.string,
            color_text: PropTypes.string,
        }),
    ),
    priceOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            price: PropTypes.number,
            pricePromo: PropTypes.number,
            type: PropTypes.number,
            value: PropTypes.number,
        }),
    ),
    spec: PropTypes.arrayOf(
        PropTypes.shape({
            asIs: PropTypes.number,
            name: PropTypes.string,
            title: PropTypes.string,
            type: PropTypes.string,
            value: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
        }),
    ),
    route: PropTypes.string,
    body: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    imageWebp: PropTypes.string,
    orderText: PropTypes.string,
    dataTest: PropTypes.string,
    isDarkTheme: PropTypes.bool,
    backgroundColorData: PropTypes.shape({
        bgImg: PropTypes.string,
        bgCard: PropTypes.string,
    }),
    baseRoute: PropTypes.string,
    linkUrl: PropTypes.string,
    setIndexOfSelected: PropTypes.func,
};

const mapState = state => ({
    city: state.city.currentCity,
});

export default connect(mapState)(memo(ProductCard));
